import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageUp, Loader2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { listMedia, type MediaAsset, type MediaFolder, uploadMedia } from '@/lib/media';
import { cn } from '@/lib/utils';

/**
 * Pick an image from the media library, or upload one on the spot.
 *
 * Every screen that stores a single image address -- a post's cover, the
 * workspace logo, the favicon -- wants the same two things, and neither is
 * worth building twice. The picked value is a URL rather than an id because
 * that is what the columns hold and what the storefront renders; the asset
 * id comes back alongside for the tables that keep both.
 */
export function MediaPicker({
  accountId,
  value,
  onChange,
  folder = 'content',
  disabled,
  label = 'Choose image',
  className,
}: {
  accountId: string;
  value: string | null;
  onChange: (picked: { url: string; assetId: string | null } | null) => void;
  folder?: MediaFolder;
  disabled?: boolean;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {value ? (
        <div className="relative w-fit">
          <img
            src={value}
            alt=""
            className="bg-muted h-32 w-auto rounded-lg border object-contain"
          />
          {!disabled && (
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="absolute -top-2 -right-2 size-6 rounded-full"
              onClick={() => onChange(null)}
            >
              <X className="size-3" />
              <span className="sr-only">Remove</span>
            </Button>
          )}
        </div>
      ) : null}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <ImageUp className="size-4" />
        {value ? 'Replace' : label}
      </Button>

      <MediaPickerDialog
        accountId={accountId}
        folder={folder}
        open={open}
        onOpenChange={setOpen}
        onPick={(asset) => {
          onChange(asset.publicUrl ? { url: asset.publicUrl, assetId: asset.id } : null);
          setOpen(false);
        }}
      />
    </div>
  );
}

function MediaPickerDialog({
  accountId,
  folder,
  open,
  onOpenChange,
  onPick,
}: {
  accountId: string;
  folder: MediaFolder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (asset: MediaAsset) => void;
}) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);

  const assets = useQuery({
    queryKey: ['media', accountId],
    queryFn: () => listMedia(accountId),
    // Only fetched once the dialog is open: the library can be hundreds of
    // rows and most screens never open the picker at all.
    enabled: open && Boolean(accountId),
  });

  const needle = search.trim().toLowerCase();

  const visible = (assets.data ?? []).filter(
    (asset) =>
      asset.publicUrl &&
      (!needle ||
        asset.filename.toLowerCase().includes(needle) ||
        asset.tags.some((tag) => tag.toLowerCase().includes(needle))),
  );

  async function handleFiles(files: FileList | null) {
    const file = files?.[0];

    if (!file) return;

    setUploading(true);

    try {
      const uploaded = await uploadMedia(accountId, file, folder);

      queryClient.invalidateQueries({ queryKey: ['media', accountId] });

      if (uploaded.asset) onPick(uploaded.asset);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Media library</DialogTitle>
          <DialogDescription>Pick an image, or upload a new one.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Input
            placeholder="Search files and tags"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}>
            {uploading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <ImageUp className="size-4" />
            )}
            Upload
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => handleFiles(event.target.files)}
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {assets.isLoading ? (
            <p className="text-muted-foreground py-8 text-center text-sm">Loading…</p>
          ) : visible.length ? (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {visible.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => onPick(asset)}
                  className="hover:border-primary focus-visible:ring-ring overflow-hidden rounded-lg border text-left focus-visible:ring-2 focus-visible:outline-none"
                >
                  <img
                    src={asset.publicUrl ?? ''}
                    alt={asset.alt ?? asset.filename}
                    loading="lazy"
                    className="bg-muted aspect-square w-full object-cover"
                  />
                  <p className="truncate p-2 text-xs">{asset.filename}</p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {assets.data?.length ? 'Nothing matches that search.' : 'The library is empty.'}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
